import React, { useCallback, useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { Modal } from '../common/Modal';
import WithRecaptcha from '../common/WithRecaptcha';
import { TEXTS } from '../../core/texts';
import { Lock } from '@mui/icons-material';
import { ValidationErrorState } from '../../types/validationErrorState';
import { Validator } from '../../core/Validator';
import { ButtonWithLoader } from '../common/ButtonWithLoader';
import { FlexBoxFullWidth } from '../common/boxes';
import { useMutation } from '@tanstack/react-query';
import { AdminAuthService } from '../../services/admin-auth';
import { useNotification } from '../../+state/useNotification';
import { AxiosErrorResponse } from '../../interfaces/errorResponse';
import { parseAxiosError } from '../../services/axios';
import { CancelButton } from '../common/CancelButton';

const AdminForgotPassword = () => {
  const [openedModal, setOpenedModal] = useState(false);
  const { openErrorToast } = useNotification();
  const [validError, setValidError] = useState<ValidationErrorState>({});
  const [guideMsg, setGuideMsg] = useState<undefined | string>(undefined);

  const toggleModal = useCallback(() => setOpenedModal(prev => {
    if (prev) {
      setValidError({});
    }
    return !prev;
  }), []);

  const mutation = useMutation({
    mutationFn: (email: string) => AdminAuthService.forgotPassword(email),
    onSuccess: () => setGuideMsg(TEXTS.RECOVER_EMAIL_SENT),
    onError: (err: AxiosErrorResponse) => {
      const msg = parseAxiosError(err);
      openErrorToast(msg);
      setValidError({ email: msg });
    }
  });

  const submitFormHandler = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const email = (e.target as any).email?.value || '';
    const validationErrors = Validator.validate({ email }, Validator.NAMES.AUTH);
    if (validationErrors) {
      return setValidError(validationErrors);
    }
    mutation.mutate(email);
  }, [mutation]);

  return (
    <WithRecaptcha>
      <Box>
        <Button variant={'outlined'} onClick={toggleModal}>{TEXTS.FORGOT_ADMIN_PASSWORD}</Button>
        {
          openedModal &&
          <Modal onClose={toggleModal} maxWidth={500} withDividers={false} title={TEXTS.FORGOT_ADMIN_PASSWORD}
                 headerIcon={<Lock color={'primary'}/>}
          >
            {
              guideMsg
                ? <Box>
                  <Typography variant={'h5'}>
                    {TEXTS.RECOVER_EMAIL_SENT}
                  </Typography>
                  <Box mt={2}/>
                  <Typography>
                    {TEXTS.RECOVER_EMAIL_SENT_DETAILS}
                  </Typography>
                </Box>
                : <>
                  <form onSubmit={submitFormHandler}>
                    <TextField sx={{ my: 2 }} name={'email'} placeholder={TEXTS.EMAIL}
                                   type={'email'} required={true} error={!!validError['email']}
                                   helperText={validError['email']} fullWidth
                    />
                    <FlexBoxFullWidth justifyContent={'space-between'} mt={2}>
                      <CancelButton onClick={toggleModal}/>
                      <ButtonWithLoader type={'submit'} color={'primary'} variant={'outlined'}
                                        loading={mutation.isLoading}
                      >
                        {TEXTS.CONFIRM}
                      </ButtonWithLoader>
                    </FlexBoxFullWidth>
                  </form>
                </>
            }
          </Modal>
        }
      </Box>
    </WithRecaptcha>
  );
};

export default AdminForgotPassword;